import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Rating,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  DirectionsCar as CarIcon,
  Search as SearchIcon,
  ViewList as ListIcon,
  Map as MapIcon,
  Navigation as NavigationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useBooking } from '../contexts/BookingContext.jsx';
import { apiService } from '../services/api.js';
import GoogleMapComponent from '../components/GoogleMapComponent.jsx';

const GaragePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchPostcode, setSearchPostcode] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [postcodeValidationError, setPostcodeValidationError] = useState('');
  const hasLoadedRef = useRef(false);
  const { 
    vehicleRegistration,
    vehicleData,
    postcode,
    coordinates,
    garages,
    setGarages,
    appendGarages,
    selectGarage,
    setLoading,
    setError,
    clearError,
    isLoading,
    error,
    setPostcode,
    setCoordinates,
    selectedProducts,
    selectedAddons
  } = useBooking();

  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);

  const loadGarages = useCallback(async (postcodeToSearch) => {
    if (!postcodeToSearch || isLoading) return;
    
    setLoading(true);
    clearError();
    setCurrentOffset(0);

    try {
      // Extract service IDs from selected services
      const productIds = selectedProducts.map(p => p.product_id);
      const addonIds = selectedAddons.map(a => a.addon_id);
      
      const response = await apiService.searchGarages(postcodeToSearch, 5, 0, productIds, addonIds);
      const garageData = response.data.garages;
      const hasMoreResults = response.data.has_more;
      
      setGarages(garageData);
      setHasMore(hasMoreResults);
      setCurrentOffset(5);
      
      // Update the booking context postcode on successful search
      setPostcode(postcodeToSearch);
      
      // Extract and set coordinates from API response
      if (response.data.search_location) {
        setCoordinates({
          latitude: response.data.search_location.latitude,
          longitude: response.data.search_location.longitude
        });
      }
      
      if (garageData.length === 0) {
        setError('No garages found within 5 miles of this postcode. Try a different postcode.');
      }
    } catch (error) {
      console.error('Error loading garages:', error);
      setGarages([]);
      if (error.response?.status === 400) {
        setError('Could not find coordinates for the provided postcode. Please check and try again.');
      } else {
        setError('Failed to load garages. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [setGarages, setError, setLoading, clearError, isLoading, setPostcode, setCoordinates]);

  const loadMoreGarages = useCallback(async () => {
    setLoadingMore(true);

    try {
      // Extract service IDs from selected services
      const productIds = selectedProducts.map(p => p.product_id);
      const addonIds = selectedAddons.map(a => a.addon_id);
      
      const response = await apiService.searchGarages(searchPostcode, 5, currentOffset, productIds, addonIds);
      const garageData = response.data.garages;
      const hasMoreResults = response.data.has_more;
      
      appendGarages(garageData);
      setCurrentOffset(currentOffset + 5);
      setHasMore(hasMoreResults);
      
    } catch (error) {
      console.error('Error loading more garages:', error);
      setError('Failed to load more garages. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  }, [searchPostcode, currentOffset, appendGarages, setError]);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      clearError();
      setSearchPostcode(postcode);
      loadGarages(postcode);
      hasLoadedRef.current = true;
    }
  }, [postcode]);


  const validatePostcode = (postcodeValue) => {
    const postcodePattern = /^[A-Z]{1,2}[0-9R][0-9A-Z]?\s?[0-9][A-Z]{2}$/;
    
    if (!postcodeValue.trim()) {
      return t('validation.required');
    } else if (!postcodePattern.test(postcodeValue.replace(/\s/g, '').toUpperCase())) {
      return t('validation.invalidPostcode');
    }
    
    return '';
  };

  const handlePostcodeSearch = () => {
    const validationError = validatePostcode(searchPostcode);
    
    if (validationError) {
      setPostcodeValidationError(validationError);
      return;
    }
    
    setPostcodeValidationError('');
    clearError();
    loadGarages(searchPostcode.trim());
  };

  const handlePostcodeChange = (event) => {
    const value = event.target.value.toUpperCase();
    setSearchPostcode(value);
    setPostcodeValidationError('');
    clearError();
  };

  const handleSelectGarage = (garage) => {
    selectGarage(garage);
    navigate('/appointment');
  };

  const formatDistance = (distance) => {
    return `${(distance || 0).toFixed(1)} miles`;
  };

  const formatPrice = (price) => {
    return `£${(price || 54.85).toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography>{t('common.loading')}</Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="/" 
          onClick={(e) => { 
            e.preventDefault(); 
            clearError(); 
            navigate('/'); 
          }}
          sx={{ cursor: 'pointer' }}
        >
          {t('nav.home')}
        </Link>
        <Typography color="text.primary">Find Garages</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>
          {t('garage.title')}
        </Typography>
      </Box>
      
      {/* Vehicle Details Section - Full width responsive */}
      <Card elevation={2} sx={{ mb: 3, width: '100%' }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          {/* Registration and Not your vehicle link - same line */}
          <Box sx={{ mb: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'nowrap' }}>
              <CarIcon color="primary" sx={{ fontSize: { xs: 24, sm: 28 }, flexShrink: 0 }} />
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, flexShrink: 0 }}>
                {vehicleRegistration}
              </Typography>
              
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  clearError();
                  navigate('/', { 
                    state: { 
                      vehicleReg: vehicleRegistration,
                      postcode: postcode 
                    }
                  });
                }}
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  p: 0,
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ml: 1,
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'transparent'
                  }
                }}
              >
                Not your vehicle?
              </Button>
            </Box>
          </Box>
          
          {/* Vehicle details and MOT expiry - responsive flex wrap */}
          {vehicleData && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              flexWrap: 'wrap',
              width: '100%'
            }}>
              <Typography variant="body2" color="text.secondary">
                {vehicleData.make}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vehicleData.year}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vehicleData.fuel_type}
              </Typography>
              
              {/* MOT Expiry Chip - can wrap to next line */}
              {vehicleData?.mot_expiry_date && (
                <Chip 
                  label={`MOT expires on ${new Date(vehicleData.mot_expiry_date).toLocaleDateString('en-GB')}`}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ 
                    fontWeight: 'medium',
                    fontSize: '0.875rem',
                    flexShrink: 0
                  }}
                />
              )}
            </Box>
          )}
          
          {/* Selected Services Section */}
          {(selectedProducts.length > 0 || selectedAddons.length > 0) && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'primary.main' }}>
                Selected Services
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedProducts.map((selected) => (
                  <Chip
                    key={selected.product_id}
                    label={selected.product.name}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 'medium' }}
                  />
                ))}
                {selectedAddons.map((selected) => (
                  <Chip
                    key={selected.addon_id}
                    label={selected.addon.name}
                    color="secondary"
                    size="small"
                    sx={{ fontWeight: 'medium' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}


      {/* Full Width Search Section - Always Visible */}
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        mb: 4,
        px: 0
      }}>
            <TextField
              value={searchPostcode}
              onChange={handlePostcodeChange}
              placeholder="Search by postcode (e.g., SW1A 1AA)"
              variant="outlined"
              size="large"
              fullWidth
              error={!!postcodeValidationError}
              helperText={postcodeValidationError}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  background: 'background.paper',
                  borderRadius: 3,
                  fontSize: '1.2rem',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
                  },
                  '&.Mui-focused': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1), 0 4px 12px rgba(25, 118, 210, 0.15)',
                  },
                  '& fieldset': {
                    border: 'none',
                  }
                },
                '& .MuiInputBase-input': {
                  py: 3,
                  px: 3,
                  fontSize: '1.2rem',
                  fontWeight: '500',
                  letterSpacing: '0.02em',
                  '&::placeholder': {
                    color: 'text.secondary',
                    opacity: 0.7,
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <LocationIcon color="primary" sx={{ fontSize: 28 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={handlePostcodeSearch}
                      disabled={isLoading || !searchPostcode.trim()}
                      edge="end"
                      size="large"
                      sx={{ 
                        color: 'primary.main',
                        borderRadius: 3,
                        px: 3,
                        py: 1,
                        mr: 1,
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        transition: 'all 0.2s ease',
                        '&:hover:not(.Mui-disabled)': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          transform: 'scale(1.05)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: 'primary.main',
                          opacity: 0.7
                        }
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon sx={{ fontSize: 28 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && searchPostcode.trim()) {
                  handlePostcodeSearch();
                }
              }}
            />
      </Box>
      
      {/* No Garages Found Message */}
      {!isLoading && garages.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          px: 4
        }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No garages found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try searching with a different postcode
          </Typography>
        </Box>
      )}
      
      {/* Results Section with Toggle */}
      {garages.length > 0 && (
          <Box sx={{ 
            position: 'relative', 
            mt: 2,
            width: '100%',
            maxWidth: '100%',
            px: 2
          }}>
            {/* View Toggle - Positioned at top right of results */}
            <Box sx={{ 
              position: 'absolute',
              top: 0,
              right: 4,
              zIndex: 10,
              mb: 2
            }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                  }
                }}
                aria-label="view mode"
                size="small"
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 3,
                  boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  overflow: 'hidden',
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    px: 3,
                    py: 1.5,
                    color: 'text.secondary',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    minWidth: 80,
                    textTransform: 'none',
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      transform: 'scale(1.02)',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    },
                    '&:hover:not(.Mui-selected)': {
                      backgroundColor: 'primary.light',
                      color: 'primary.main',
                      transform: 'translateY(-1px)',
                    }
                  }
                }}
              >
                <ToggleButton value="list" aria-label="list view">
                  <ListIcon sx={{ mr: 1 }} fontSize="small" />
                  List
                </ToggleButton>
                <ToggleButton value="map" aria-label="map view">
                  <MapIcon sx={{ mr: 1 }} fontSize="small" />
                  Map
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {/* Results Content */}
            <Box sx={{ pt: 8 }}>
            {viewMode === 'list' ? (
            /* List View - Single Column */
            <>
            <Box sx={{ width: '100%' }}>
              {garages.map((garage) => (
                <Card 
                  key={garage.id}
                  elevation={2}
                  sx={{ 
                    mb: 3,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 4 }, m: { xs: 1, sm: 2 } }}>
                    {/* Mobile Layout */}
                    <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold',
                        fontSize: '1.25rem',
                        mb: 0.5
                      }}>
                        {garage.name}
                      </Typography>
                      
                      {/* Address */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 0 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" sx={{
                          fontSize: '0.8rem',
                          lineHeight: 1.3
                        }}>
                          {garage.address}, {garage.postcode}
                        </Typography>
                      </Box>
                      
                      
                      {/* Distance */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <NavigationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" sx={{
                          fontSize: '0.8rem',
                          lineHeight: 1.3
                        }}>
                          {formatDistance(garage.distance_miles)} away
                        </Typography>
                      </Box>
                      
                      {/* Rating */}
                      {garage.rating && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating value={garage.rating} precision={0.1} size="small" readOnly />
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            fontSize: '0.8rem'
                          }}>
                            {garage.rating} ({garage.total_reviews || 0} reviews)
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Desktop Two-Column Layout */}
                    <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: '2fr 1fr', gap: 4, alignItems: 'start' }}>
                      {/* Left Column - Details */}
                      <Box>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 'bold',
                          fontSize: '1.5rem',
                          mb: 1
                        }}>
                          {garage.name}
                        </Typography>
                        
                        {/* Address */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary" sx={{
                            fontSize: '0.875rem',
                            lineHeight: 1.5
                          }}>
                            {garage.address}, {garage.postcode}
                          </Typography>
                        </Box>
                        
                        
                        {/* Distance */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <NavigationIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary" sx={{
                            fontSize: '0.875rem',
                            lineHeight: 1.5
                          }}>
                            {formatDistance(garage.distance_miles)} away
                          </Typography>
                        </Box>
                        
                        {/* Rating */}
                        {garage.rating && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Rating value={garage.rating} precision={0.1} size="small" readOnly />
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              fontSize: '0.875rem'
                            }}>
                              {garage.rating} ({garage.total_reviews || 0} reviews)
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Right Column - Price and Select */}
                      <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h4" color="primary.main" sx={{ 
                          fontWeight: 'bold',
                          fontSize: '2rem'
                        }}>
                          {formatPrice(garage.total_price)}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={() => handleSelectGarage(garage)}
                          sx={{ 
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            minWidth: 'fit-content',
                            '&.Mui-disabled': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              opacity: 0.8
                            }
                          }}
                        >
                          Select
                        </Button>
                      </Box>
                    </Box>

                    {/* Facilities */}
                    {garage.facilities && garage.facilities.length > 0 && (
                      <Box sx={{ mb: 2, mt: { xs: 3, sm: 3 }, pt: { xs: 2, sm: 2 }, borderTop: '1px solid', borderColor: 'grey.200' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                          Facilities:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {garage.facilities.map((facility, index) => (
                            <Chip 
                              key={index}
                              label={facility}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Price and Select Button - Mobile only (after facilities) */}
                    <Box sx={{ 
                      display: { xs: 'flex', sm: 'none' },
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 3,
                      pt: 3,
                      borderTop: '2px solid',
                      borderColor: 'primary.light',
                      backgroundColor: 'rgba(25, 118, 210, 0.02)',
                      mx: -2,
                      px: 3,
                      borderRadius: '0 0 12px 12px'
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h4" color="primary.main" sx={{ 
                          fontWeight: 'bold',
                          fontSize: '1.8rem',
                          lineHeight: 1
                        }}>
                          {formatPrice(garage.total_price)}
                        </Typography>
                      </Box>
                      
                      <Button
                        variant="contained"
                        onClick={() => handleSelectGarage(garage)}
                        sx={{ 
                          px: 4,
                          py: 1.5,
                          fontSize: '1rem',
                          minWidth: 'fit-content',
                          '&.Mui-disabled': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            opacity: 0.8
                          }
                        }}
                      >
                        Select
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
            
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Button 
                  variant="outlined" 
                  size="large"
                  onClick={loadMoreGarages}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Show more'}
                </Button>
              </Box>
            )}
            </>
          ) : (
            /* Map View */
            <GoogleMapComponent 
              garages={garages}
              center={coordinates}
              onGarageSelect={handleSelectGarage}
              formatDistance={formatDistance}
              formatPrice={formatPrice}
            />
          )}
          
          <Box sx={{ mt: 3, mb: 2 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Search Location:</strong> {searchPostcode}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Showing garages within 5 miles
              </Typography>
            </Alert>
          </Box>
            </Box>
          </Box>
      )}
    </Container>
  );
};

export default GaragePage;